import { useState, useEffect, useCallback } from 'react';

/**
 * A useState wrapper that persists state to localStorage.
 * Reads the initial value from localStorage (falling back to defaultValue),
 * and writes back on every state change.
 */
export function useLocalStorage<T>(
  key: string,
  defaultValue: T
): [T, (value: T | ((prev: T) => T)) => void] {
  const [state, setState] = useState<T>(() => {
    try {
      const stored = localStorage.getItem(key);
      if (stored !== null) {
        return JSON.parse(stored) as T;
      }
    } catch {
      // Corrupted or unreadable — fall through to default
    }
    return defaultValue;
  });

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(state));
    } catch {
      // Storage full or unavailable — silently ignore
    }
  }, [key, state]);

  // Allow external code (e.g. project loader) to update this hook's state
  // by dispatching a 'synthkitty:storage-update' CustomEvent.
  useEffect(() => {
    const handler = (e: Event) => {
      const { key: k, value } = (e as CustomEvent<{ key: string; value: string }>).detail;
      if (k !== key) return;
      try {
        setState(JSON.parse(value) as T);
      } catch {
        // Ignore malformed payloads
      }
    };
    window.addEventListener('synthkitty:storage-update', handler);
    return () => window.removeEventListener('synthkitty:storage-update', handler);
  }, [key]);

  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      setState(value);
    },
    []
  );

  return [state, setValue];
}
